<script setup lang="ts">
/**
 * KEBIJAKAN PRIVASI — tema v3.
 *
 * Isinya WAJIB mencerminkan perilaku sistem yang sebenarnya:
 *  - berkas job dihapus <= 24 jam (internal/jobs/cleanup.go, RetensiDefault)
 *  - alat gratis diproses di memori, tidak menyentuh disk (internal/httpapi/gratis.go)
 *  - email pembeli bersifat opsional (internal/httpapi/checkout.go)
 *  - alamat IP dipakai untuk rate limit in-memory (internal/httpapi/server.go)
 *  - sesi pemakaian berumur 7 hari di Redis (internal/httpapi/session.go)
 * Jangan menambah klaim yang belum benar (mis. enkripsi khusus, sertifikasi).
 *
 * Seluruh kalimat dibawa PERSIS dari app/pages/privasi.vue; yang berubah hanya
 * tampilannya. Canonical menunjuk alamat produksi '/privasi' selama v3 masih
 * prototipe — lihat catatan lengkap di v3/kompres-foto/index.vue.
 */

const situs = useSitusUrl()
const identitas = useIdentitas()

pakaiSeo({
  judul: 'Kebijakan Privasi — tools.coreasia.id',
  deskripsi:
    'Data apa yang kami kumpulkan, berapa lama disimpan, dan cara meminta penghapusan. Berkas unggahan dihapus otomatis paling lama 24 jam.',
  path: '/privasi',
  jsonLd: [
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['Kebijakan Privasi', '/privasi']
    ])
  ]
})
</script>

<template>
  <CaHalamanLegal judul="Kebijakan Privasi" :terakhir="TANGGAL_KEBIJAKAN" ikon="i-lucide-shield">
    <template #pembuka>
      Halaman ini menjelaskan data apa yang kami kumpulkan saat kamu memakai tools.coreasia.id,
      berapa lama data itu kami simpan, dan bagaimana cara memintanya dihapus. Kami menulisnya
      sesuai cara kerja sistem yang sebenarnya, bukan salinan template.
    </template>

    <CaCard>
      <h2 class="ca-h3 text-ink">1. Siapa yang mengelola layanan ini</h2>
      <CaProsa v-if="identitas.adaNama" class="mt-3">
        <p>
          Layanan tools.coreasia.id dikelola oleh <strong>{{ identitas.namaBadanUsaha }}</strong
          ><span v-if="identitas.adaAlamat">, beralamat di {{ identitas.alamat }}</span>.
        </p>
        <p v-if="identitas.pseTerdaftar">
          Layanan ini terdaftar sebagai Penyelenggara Sistem Elektronik (PSE) pada Kementerian
          Komunikasi dan Digital.
        </p>
      </CaProsa>
      <CaAlert v-else tone="warn" title="Identitas pengelola belum dicantumkan" class="mt-3">
        Jangan lakukan pembayaran sebelum bagian ini terisi.
      </CaAlert>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">2. Data yang kami kumpulkan</h2>
      <CaProsa class="mt-3">
        <h3>Berkas yang kamu unggah</h3>
        <p>
          Untuk layanan berbayar (BerkasKilat, RapiKontak), berkas yang kamu unggah disimpan
          sementara di server kami selama pemrosesan, bersama hasil olahannya. Berkas pendaftaran
          sering memuat data pribadi — KTP, kartu keluarga, ijazah, pas foto — jadi umurnya di
          server kami batasi ketat (lihat bagian 3).
        </p>
        <p>
          Untuk alat gratis (kompres foto dan kompres PDF), berkas diproses langsung di memori
          server dan <strong>tidak pernah ditulis ke penyimpanan</strong>. Setelah hasilnya dikirim
          balik ke perambanmu, salinannya di server hilang.
        </p>

        <h3>Alamat email — opsional</h3>
        <p>
          Saat membeli, kolom email boleh dikosongkan. Bila kamu mengisinya, email itu dipakai untuk
          dua hal saja: mengirim kode aktivasi dan menjawab bila kamu menghubungi kami soal pesanan
          tersebut. Kami tidak mengirim buletin atau materi promosi.
        </p>

        <h3>Ebook</h3>
        <p>
          Pembelian ebook berbeda dari paket alat, dan datanya lebih banyak. Kami menuliskannya
          satu per satu:
        </p>
        <ul>
          <li>
            <strong>Email wajib diisi</strong>, karena tautan unduh bukunya dikirim ke sana dan tidak
            ada jalan lain untuk menerimanya.
          </li>
          <li>
            <strong>Nama boleh dikosongkan.</strong> Bila diisi, nama dan emailmu dicetak di setiap
            halaman PDF bersama kode lisensi, sebagai penanda kepemilikan berkas.
          </li>
          <li>
            <strong>Berkas PDF atas namamu</strong> disimpan di penyimpanan objek kami supaya bisa
            diunduh ulang lewat tautan yang sah.
          </li>
          <li>
            <strong>Setiap unduhan dicatat</strong>: waktu, alamat IP, dan jenis perambannya. Catatan
            ini dipakai untuk membatasi penyalahgunaan tautan yang tersebar.
          </li>
          <li>
            <strong>Permintaan sampel gratis:</strong> email yang kamu isi hanya dipakai untuk
            mengirim sampel. Kami mencatat email itu, judul bukunya, berapa kali diminta, dan
            halaman asal permintaannya. Kami tidak mengirim promosi ke alamat tersebut.
          </li>
        </ul>

        <h3>Alamat IP</h3>
        <p>
          Alamat IP-mu dibaca untuk membatasi jumlah permintaan per menit, agar layanan tidak
          diserbu permintaan otomatis dan tetap bisa dipakai orang lain. Hitungan ini disimpan di
          memori server dan hilang saat layanan dimuat ulang. Selain itu, server web mencatat log
          akses teknis (alamat IP, waktu, alamat halaman, kode status) sebagaimana lazimnya server
          web, untuk keperluan keamanan dan penelusuran gangguan.
        </p>

        <h3>Data pembayaran</h3>
        <p>
          Kami <strong>tidak pernah</strong> menerima, melihat, atau menyimpan nomor kartu, PIN,
          atau kredensial rekeningmu. Pembayaran QRIS diproses sepenuhnya oleh penyelenggara
          pembayaran yang kami gunakan. Yang kami simpan hanya catatan pesanan: nomor pesanan, jenis
          paket, jumlah tagihan, status pembayaran, dan waktunya.
        </p>

        <h3>Yang tidak kami kumpulkan</h3>
        <p>
          Tidak ada pendaftaran akun, jadi kami tidak menyimpan nomor telepon atau kata sandi. Nama
          hanya kami simpan bila kamu mengisinya saat membeli ebook. Kami juga tidak memasang pelacak iklan pihak ketiga di halaman ini.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">3. Berapa lama data disimpan</h2>
      <CaProsa class="mt-3">
        <ul>
          <li>
            <strong>Berkas unggahan dan hasil olahannya:</strong> dihapus otomatis paling lama
            {{ RETENSI_BERKAS_JAM }} jam setelah diproses. Penghapusan dijalankan berkala oleh
            sistem, tanpa perlu kamu minta. Karena itu, unduh hasilnya segera setelah selesai.
          </li>
          <li><strong>Berkas alat gratis:</strong> tidak disimpan sama sekali.</li>
          <li>
            <strong>Sesi pemakaian setelah menukar kode:</strong> berumur 7 hari, lalu kedaluwarsa
            sendiri.
          </li>
          <li>
            <strong>Kode aktivasi dan catatan pesanan:</strong> disimpan selama masih dibutuhkan
            untuk membuktikan pembelian, menangani keluhan, dan memenuhi kewajiban pembukuan.
          </li>
          <li>
            <strong>Alamat email pembeli:</strong> disimpan menempel pada catatan pesanan yang
            bersangkutan, dan hanya selama catatan itu masih disimpan.
          </li>
          <li>
            <strong>Lisensi ebook, berkas PDF atas namamu, dan catatan unduhannya:</strong>
            disimpan selama lisensinya berlaku, karena tanpa itu buku yang sudah kamu beli tidak
            bisa diunduh ulang.
          </li>
          <li>
            <strong>Email permintaan sampel:</strong> disimpan sampai kamu meminta dihapus lewat
            kanal kontak di halaman ini.
          </li>
        </ul>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">4. Dengan siapa data dibagikan</h2>
      <CaProsa class="mt-3">
        <p>
          Kami tidak menjual, menyewakan, atau menukarkan datamu. Data hanya diteruskan kepada pihak
          yang benar-benar diperlukan agar layanan berjalan:
        </p>
        <ul>
          <li>
            <strong>Penyelenggara pembayaran QRIS</strong> — menerima nomor pesanan dan jumlah
            tagihan untuk menerbitkan kode QR dan mengonfirmasi pembayaran.
          </li>
          <li>
            <strong>Penyedia pengiriman email</strong> — menerima alamat emailmu dan isi pesan
            berisi kode aktivasi atau tautan unduh ebook.
          </li>
          <li>
            <strong>Penyedia server dan penyimpanan objek</strong> — tempat layanan ini berjalan,
            berkas alat tersimpan sementara, dan berkas ebook pembeli disimpan.
          </li>
        </ul>
        <p>
          Di luar itu, data hanya kami serahkan bila diwajibkan oleh hukum atau permintaan resmi
          aparat penegak hukum yang sah.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">5. Hakmu atas datamu</h2>
      <CaProsa class="mt-3">
        <p>
          Kamu berhak meminta salinan data yang kami simpan tentangmu, meminta koreksi, atau meminta
          penghapusan. Karena layanan ini tanpa akun, sebutkan penanda yang kami punya agar datamu
          bisa ditemukan: kode aktivasi, nomor pesanan, atau alamat email yang kamu pakai saat
          membeli.
        </p>
        <p>
          Perlu diketahui: berkas unggahan yang sudah lewat {{ RETENSI_BERKAS_JAM }} jam sudah
          terhapus dengan sendirinya, jadi permintaan penghapusan berkas biasanya sudah terpenuhi
          sebelum sempat diajukan. Untuk catatan pesanan, sebagian mungkin tetap kami simpan bila
          masih dibutuhkan sebagai bukti transaksi.
        </p>
        <p v-if="identitas.adaKontak">Ajukan permintaanmu lewat kanal berikut:</p>
      </CaProsa>

      <CaKontak
        class="mt-4"
        konteks="Kebijakan Privasi"
        pesan="Halo, saya ingin mengajukan permintaan terkait data pribadi saya."
        subjek="Permintaan data pribadi — tools.coreasia.id"
      />
      <CaAlert v-if="!identitas.adaKontak" tone="warn" title="Kanal kontak belum tersedia" class="mt-4">
        Kanal kontak resmi belum dicantumkan pada halaman ini.
      </CaAlert>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">6. Perubahan kebijakan</h2>
      <CaProsa class="mt-3">
        <p>
          Bila kebijakan ini berubah, tanggal "terakhir diperbarui" di bagian atas halaman ikut
          berubah. Kami menyarankan kamu memeriksa halaman ini sebelum membeli.
        </p>
      </CaProsa>
    </CaCard>

    <template #tautan>
      <NuxtLink to="/syarat" class="text-brand font-semibold underline underline-offset-2">
        Syarat Layanan
      </NuxtLink>
      dan
      <NuxtLink to="/refund" class="text-brand font-semibold underline underline-offset-2">
        Kebijakan Pengembalian Dana
      </NuxtLink>.
    </template>
  </CaHalamanLegal>
</template>
